import { useId, useRef, useState } from 'react';
import { ErrorNotice } from '@bcecloud/portal-ui';
import { uploadMedia, errorMessage } from '@/lib/api';

export interface ImageUploadFieldProps {
  /** Field label, e.g. "Logo". */
  readonly label: string;
  /** The current image URL (stored on the brand). Empty when none. */
  readonly value: string;
  /** Called with the new URL after upload, or '' when cleared. */
  readonly onChange: (url: string) => void;
  /** The client this asset belongs to, so uploads are grouped by org. */
  readonly organizationId: string;
  /** What the asset is used as, e.g. 'logo' | 'hero'. */
  readonly kind: string;
  /** Optional helper text under the control. */
  readonly hint?: string;
}

/**
 * Uploads a branding image to S3 and stores the resulting URL.
 *
 * Upload-first — the client's account team picks a file rather than hunting for
 * a hosted URL — but a URL can still be pasted for an asset already on a CDN, so
 * existing URL-based brands stay editable. The stored value is always a URL, so
 * nothing downstream (the brand record, the client portal's rendering) changes.
 *
 * @param props - Label, current value, change handler, and upload scope.
 * @returns The image upload control.
 */
export function ImageUploadField({
  label,
  value,
  onChange,
  organizationId,
  kind,
  hint,
}: ImageUploadFieldProps): React.ReactElement {
  const inputId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUrl, setShowUrl] = useState(false);

  async function onFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    // Reset the input so choosing the same file again still fires onChange.
    event.target.value = '';
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const media = await uploadMedia(file, { organizationId, kind });
      onChange(media.url);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <span className="mb-1.5 block text-portal-tiny font-bold">{label}</span>

      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-admin-line bg-admin-subtle">
          {value ? (
            <img src={value} alt={`${label} preview`} className="h-full w-full object-contain" />
          ) : (
            <span className="text-admin-micro text-admin-muted" aria-hidden="true">
              none
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="h-9 cursor-pointer rounded-md border border-admin-line px-3 text-sm font-semibold hover:bg-admin-subtle disabled:opacity-50"
            >
              {busy ? 'Uploading…' : value ? 'Replace' : 'Upload image'}
            </button>
            {value ? (
              <button
                type="button"
                onClick={() => onChange('')}
                disabled={busy}
                className="h-9 cursor-pointer rounded-md px-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                Remove
              </button>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setShowUrl((s) => !s)}
            className="self-start text-admin-micro text-admin-muted underline-offset-2 hover:underline"
          >
            {showUrl ? 'Hide URL field' : 'Or paste a URL'}
          </button>
        </div>

        <input
          ref={fileRef}
          id={inputId}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
          onChange={onFile}
          className="sr-only"
        />
      </div>

      {showUrl ? (
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="mt-2 h-10 w-full rounded-md border border-admin-line px-3 text-sm"
        />
      ) : null}

      {hint ? <p className="mt-1.5 text-admin-micro text-admin-muted">{hint}</p> : null}
      {error ? (
        <div className="mt-2">
          <ErrorNotice inline title="Upload failed" message={error} />
        </div>
      ) : null}
    </div>
  );
}
